import { scrollBarStyle } from '@/constants';

export const sectionStackStyling = {
    paddingTop: 8,
    bottomBorder: 'red 1px solid',
};

export const headerStyling = (darkMode) => ({
    fontSize: '24px',
    fontWeight: '400',
    lineHeight: '28.8px',
    color: darkMode ? 'white' : '#262626',
});

export const formLabelStyling = (darkMode, noPadding) => ({
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    minWidth: '246px',
    fontSize: '14px',
    fontWeight: '600',
    paddingTop: noPadding ? 0 : '22px',
    lineHeight: '19.2px',
    color: darkMode ? 'white' : null,
    marginBottom: 0,
});

export const descriptionStyling = {
    color: '#757575',
    fontSize: '11px',
    fontWeight: 400,
    lineHeight: '12px',
};

export const textBoxStyling = {
    height: '30px',
    paddingTop: '15px',
    padding: '2px 6px',
    minWidth: '240px',
    borderRadius: '5px',
};

export const stackStyling = (hasDescription) => {
    return {
        minHeight: hasDescription ? '75px' : '60px',
        // Cap at the desktop design width but let the row shrink with the
        // card on narrow viewports (iPad and below) so fields don't
        // overflow their container.
        width: '100%',
        maxWidth: '775px',
    };
};

export const breadCrumbStyling = {
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: '14px',
    letterSpacing: '0.15px',
    paddingBottom: '15px',
};

export const headingStyling = {
    fontSize: '20px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '28.8px',
    letterSpacing: '0.15px',
    paddingBottom: '15px',
};

export const subHeadingStyling = {
    fontSize: '24px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '28.8px',
    letterSpacing: '0.15px',
};

export const accordionHeadingStyling = {
    color: '#262626',
    fontSize: '1rem',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '20px',
    letterSpacing: '0.15px',
};

export const infoTextStyling = {
    fontSize: '11px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '16px',
    letterSpacing: '0.15px',
};

export const dropDownFormStyle = (darkMode, isMulti) => ({
    dropdownIndicator: (provided, { selectProps }) => {
        return {
            ...provided,
            bg: 'transparent',
            px: 2,
            cursor: 'pointer',
            '> svg': {
                transform: `rotate(${selectProps.menuIsOpen ? -180 : 0}deg)`,
                transitionDuration: '.4s',
            },
        };
    },
    container: (provided) => ({
        ...provided,
        height: isMulti ? 'auto' : '30px',
    }),
    indicatorSeparator: (provided) => ({
        ...provided,
        display: 'none',
    }),
    // Matches selectedOptionColor="brand" (passed by nearly every caller of
    // this style) with an explicit brand-coloured highlight for the selected
    // option — without it, isSelected falls through to chakra-react-select's
    // own dark-mode default (a muted grey background with black text, which
    // reads poorly against this app's dark surfaces).
    option: (provided, { isFocused, isSelected }) => ({
        ...provided,
        backgroundColor: isSelected
            ? 'brand.500'
            : darkMode && isFocused
              ? 'rgba(255, 255, 255, 0.16)!important'
              : null,
        color: isSelected ? 'white' : provided.color,
        overflow: 'visible',
        position: 'relative',
    }),
    control: (provided) => ({
        ...provided,
        background: `${darkMode ? 'dark.bgDark' : '#fff'}`,
        minHeight: '30px',
        height: isMulti ? 'auto' : '30px',
        color: `${darkMode ? 'dark.textDark' : 'inherit'}`,
        borderColor: `${darkMode ? 'dark.tcap-borderGray' : 'inherit'}`,
        // Pin the focus ring to the brand colour regardless of light/dark
        // mode — without this it falls back to chakra-react-select's
        // default (unbranded) focus ring.
        _focus: {
            ...provided._focus,
            borderColor: 'brand.500',
            boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
        },
    }),

    valueContainer: (provided) => ({
        ...provided,
        height: isMulti ? 'auto' : '30px',
        padding: '0 6px',
        background: `${darkMode ? 'dark.bgDark' : 'inherit'}`,
    }),

    input: (provided) => ({
        ...provided,
        height: isMulti ? 'auto' : '30px',
        margin: '0px',
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: isMulti ? 'auto' : '30px',
        background: `${darkMode ? 'dark.bgDark' : 'inherit'}`,
    }),
    inputContainer: (provided) => ({
        ...provided,
        background: `${darkMode ? 'dark.bgDark' : 'inherit'}`,
        color: `${darkMode ? 'dark.textDark' : 'inherit'}`,
    }),
    menuList: (provided, { selectProps }) => ({
        ...provided,
        ...scrollBarStyle(darkMode),
        background: `${
            darkMode && selectProps.onFocus ? 'dark.tcap-weak' : 'white'
        }`,
        borderColor: `${darkMode ? 'dark.tcap-borderGray' : 'inherit'}`,
        color: `${darkMode ? 'white' : 'black'}`,
        zIndex: 1001,
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 1001,
        overflow: 'visible',
    }),
});

export const stackSpacing = 75;

export const disabledTextColour = '#C8C8C8';

export const borderColor = '#565557';

export const numbersToPortStyle = {
    margin: '0 !important',
    ...stackStyling(),
    height: 'auto',
    "div[role='alert']": {
        width: '750px',
    },
    "div[role='alert'][aria-label*='info'], & div>.chakra-text": {
        display: 'none',
    },
    "td[role='gridcell']": {
        '& input': {
            height: '32px',
            minWidth: '235px',
        },
        '& input, & button svg': {
            fontSize: '14px',
        },
        '&:last-child': {
            paddingLeft: 0,
            paddingRight: '30px',
        },
        pb: '4px',
        pt: 0,
    },
    'tr th': {
        paddingBottom: 0,
        paddingTop: '4px',
        '&:last-child': {
            paddingLeft: 0,
        },
    },
    tbody: {
        paddingY: '15px',
    },
};

export const noBreadcrumbStyle = {
    '& + div nav.chakra-breadcrumb': {
        display: 'none',
    },
};

export const paddingInputStyle = {
    '& + form .chakra-stack>input.chakra-input': {
        px: '16px',
    },
};

export const formErrorStyle = {
    'div[data-cy="input-quantity"], div[data-cy="input-authorizedSignerNumber"]':
        {
            '.chakra-form__error-message': {
                mb: 4,
            },
        },
};

export const formInputStyles = {
    '& .chakra-stack input': {
        pl: 2,
    },
};

export const formSelectStyles = {
    '& .chakra-stack div[id*=react-select]': {
        zIndex: 10,
    },
};

export const formTextStyling = (darkMode) => ({
    fontSize: '12px',
    fontWeight: 500,
    overflowWrap: 'break-word',
    color: darkMode ? 'white' : null,
});

export const formErrorMessageStyling = {
    '& .chakra-form__error-message': {
        marginTop: '8px !important',
        fontSize: '14px',
        justifyContent: 'flex-start',
    },
};
